import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listOrganizationMCPServers,
  getMCPFilterOptions,
} from '@kirin/api-client/api/mcp';
import type {
  MCPFilterOptionField,
  MCPServerListItem,
  MCPSortField,
} from '@kirin/api-client/contracts/mcp';
import { apiClient } from '@/lib/api-client';

/**
 * Fetch MCP servers for the DataTable component.
 */
export async function fetchMCPServers(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<MCPServerListItem>> {
  const sortField = params.sort?.field as MCPSortField | undefined;
  const response = await listOrganizationMCPServers(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: sortField,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
    },
    { signal }
  );

  return {
    data: response.data,
    pagination: response.pagination,
    meta: {
      activeFilters: response.meta?.activeFilters ?? [],
      search: response.meta?.search,
    },
  };
}

/**
 * Fetch dynamic filter options for name, version, publisher, or users fields.
 * Returns either string[] or rich option objects depending on the field.
 */
export async function fetchMCPFieldFilterOptions(
  organizationId: string,
  field: MCPFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getMCPFilterOptions(apiClient, organizationId, {
    field,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });

  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}
